import { useCallback, useEffect, useMemo, useState } from 'react'
import { Gift, Loader2 } from 'lucide-react'
import { Link } from 'react-router-dom'

import { ReportFilterBar } from '@/components/reports/ReportFilterBar'
import {
  exportPackageSalesReport,
  fetchPackageSalesReport,
  formatReportDate,
  formatReportMoney,
  resolveReportPeriodBounds,
} from '@/lib/api/reports'
import { apiErrorMessage } from '@/lib/apiErrorMessage'
import { useBranch } from '@/contexts/BranchContext'
import { useReportPanelFilters } from '@/contexts/ReportPreferencesContext'
import { posPackageTypeLabel } from '@/lib/pos/posPackage'

export function PackageSalesReportPanel() {
  const { activeBranchId } = useBranch()
  const { period, setPeriod, compare, setCompare, customFrom, setCustomFrom, customTo, setCustomTo } =
    useReportPanelFilters()
  const [report, setReport] = useState(null)
  const [loading, setLoading] = useState(true)
  const [exporting, setExporting] = useState(false)
  const [error, setError] = useState('')

  const bounds = useMemo(() => {
    try {
      return resolveReportPeriodBounds(period, { from: customFrom, to: customTo })
    } catch {
      return null
    }
  }, [period, customFrom, customTo])

  const reportOptions = useMemo(
    () => ({
      period,
      from: period === 'custom' ? customFrom : bounds?.from,
      to: period === 'custom' ? customTo : bounds?.to,
      compare,
    }),
    [period, customFrom, customTo, bounds, compare],
  )

  const loadReport = useCallback(async () => {
    if (period === 'custom' && (!customFrom || !customTo)) {
      setReport(null)
      setLoading(false)
      return
    }

    setLoading(true)
    setError('')

    try {
      const data = await fetchPackageSalesReport(reportOptions)
      setReport(data)
    } catch (err) {
      setReport(null)
      setError(apiErrorMessage(err, 'Unable to load package sales report.'))
    } finally {
      setLoading(false)
    }
  }, [period, customFrom, customTo, reportOptions])

  useEffect(() => {
    loadReport()
  }, [loadReport, activeBranchId])

  const handleExport = async (format) => {
    setExporting(true)
    setError('')
    try {
      await exportPackageSalesReport(format, reportOptions)
    } catch (err) {
      setError(apiErrorMessage(err, `Unable to export ${format.toUpperCase()} report.`))
    } finally {
      setExporting(false)
    }
  }

  return (
    <section className="card-surface p-6 lg:p-8">
      <div className="mb-5 flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10 text-primary">
            <Gift className="h-5 w-5" aria-hidden />
          </div>
          <div>
            <h2 className="text-lg font-semibold tracking-tight">Package sales & liability</h2>
            <p className="text-sm text-muted-foreground">
              Packages sold in the selected period plus current outstanding liability snapshot.
            </p>
          </div>
        </div>
        <ReportFilterBar
          period={period}
          compare={compare}
          customFrom={customFrom}
          customTo={customTo}
          onPeriodChange={setPeriod}
          onCompareChange={setCompare}
          onCustomFromChange={setCustomFrom}
          onCustomToChange={setCustomTo}
          onExportCsv={() => handleExport('csv')}
          onExportPdf={() => handleExport('pdf')}
          exporting={exporting}
        />
      </div>

      {loading ? (
        <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading report…
        </div>
      ) : error ? (
        <p className="text-sm text-destructive" role="alert">
          {error}
        </p>
      ) : report ? (
        <>
          <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Sales in period
          </h3>
          <div className="mb-5 grid gap-3 sm:grid-cols-2">
            <SummaryCard label="Packages sold" value={String(report.sales_summary.package_count)} />
            <SummaryCard
              label="Sales revenue"
              value={`AED ${formatReportMoney(report.sales_summary.sales_revenue)}`}
            />
          </div>

          {report.compare_summary ? (
            <p className="mb-4 text-sm text-muted-foreground">
              Compare period: {report.compare_summary.package_count} packages · AED{' '}
              {formatReportMoney(report.compare_summary.sales_revenue)}
            </p>
          ) : null}

          {report.sales_by_type.length > 0 ? (
            <div className="mb-6 overflow-x-auto rounded-2xl border border-border/70">
              <table className="min-w-full text-sm">
                <thead className="bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="px-4 py-3 font-medium">Type</th>
                    <th className="px-4 py-3 font-medium">Count</th>
                    <th className="px-4 py-3 font-medium">Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  {report.sales_by_type.map((row) => (
                    <tr key={row.package_type} className="border-t border-border/60">
                      <td className="px-4 py-3">{posPackageTypeLabel(row.package_type)}</td>
                      <td className="px-4 py-3">{row.package_count}</td>
                      <td className="px-4 py-3">AED {formatReportMoney(row.sales_revenue)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}

          <div className="mb-8 overflow-x-auto rounded-2xl border border-border/70">
            <table className="min-w-full text-sm">
              <thead className="bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-4 py-3 font-medium">Date</th>
                  <th className="px-4 py-3 font-medium">Customer</th>
                  <th className="px-4 py-3 font-medium">Package</th>
                  <th className="px-4 py-3 font-medium">Type</th>
                  <th className="px-4 py-3 font-medium">Sale</th>
                  <th className="px-4 py-3 font-medium">Price</th>
                  <th className="px-4 py-3 font-medium">Outstanding</th>
                </tr>
              </thead>
              <tbody>
                {report.sales.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">
                      No package sales in this period.
                    </td>
                  </tr>
                ) : (
                  report.sales.map((row) => (
                    <tr key={row.id} className="border-t border-border/60">
                      <td className="px-4 py-3">{formatReportDate(row.purchased_at)}</td>
                      <td className="px-4 py-3">
                        <Link
                          to="/customers"
                          className="font-medium text-primary hover:underline"
                        >
                          {row.customer_name}
                        </Link>
                      </td>
                      <td className="px-4 py-3">{row.package_name}</td>
                      <td className="px-4 py-3">{posPackageTypeLabel(row.package_type)}</td>
                      <td className="px-4 py-3">{row.sale_number ?? '—'}</td>
                      <td className="px-4 py-3">AED {formatReportMoney(row.purchase_price)}</td>
                      <td className="px-4 py-3">AED {formatReportMoney(row.outstanding_liability)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Outstanding liability (current)
          </h3>
          <div className="mb-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <SummaryCard
              label="Total liability"
              value={`AED ${formatReportMoney(report.liability.total_liability)}`}
            />
            <SummaryCard label="Active packages" value={String(report.liability.package_count)} />
            <SummaryCard
              label="Purchase value"
              value={`AED ${formatReportMoney(report.liability.total_purchase_price)}`}
            />
            <SummaryCard
              label="Recognized revenue"
              value={`AED ${formatReportMoney(report.liability.total_recognized)}`}
            />
          </div>

          {report.liability.by_type.length > 0 ? (
            <div className="overflow-x-auto rounded-2xl border border-border/70">
              <table className="min-w-full text-sm">
                <thead className="bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="px-4 py-3 font-medium">Type</th>
                    <th className="px-4 py-3 font-medium">Packages</th>
                    <th className="px-4 py-3 font-medium">Outstanding</th>
                  </tr>
                </thead>
                <tbody>
                  {report.liability.by_type.map((row) => (
                    <tr key={row.package_type} className="border-t border-border/60">
                      <td className="px-4 py-3">{posPackageTypeLabel(row.package_type)}</td>
                      <td className="px-4 py-3">{row.package_count}</td>
                      <td className="px-4 py-3">AED {formatReportMoney(row.outstanding_liability)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </>
      ) : null}
    </section>
  )
}

/**
 * @param {{ label: string, value: string }} props
 */
function SummaryCard({ label, value }) {
  return (
    <div className="rounded-2xl border border-border/70 bg-muted/20 px-4 py-3">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-1 text-lg font-semibold">{value}</p>
    </div>
  )
}
